import * as d3 from 'd3';
import i18next from 'i18next';
import { rightRoundedRect, topRoundedRect } from './svg.js';

/**
 * Create a bar chart with right-rounded bars.
 *
 * @param {Array} data The data array where each object has a `display_name` and a `count` property.
 * @param {string} id The CSS selector of the element to which the SVG should be appended.
 * @return {Object} The SVG element containing the bar chart.
 */
export function bar_chart(data, id) {
    const margin = { top: 20, right: 30, bottom: 40, left: 350 };

    let container = document.getElementById(id);
    let width = container.clientWidth; // Adjusted to fit container
    let height = width / 4;

    // SVG
    const svg = d3.select(`#${id}`)
        .append("svg")
        .attr("width", width)
        .attr("height", height);

    // Axis
    const x = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.count)])
        .nice()
        .range([margin.left, width - margin.right]);

    const y = d3.scaleBand()
        .domain(data.map(d => d.display_name))
        .range([margin.top, height - margin.bottom])
        .padding(0.1);

    // Define the gradient
    const defs = svg.append("defs");

    const gradient = defs.append("linearGradient")
        .attr("id", "gradient")
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "100%")
        .attr("y2", "0%");

    gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "rgba(0,163,164,1)");

    gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "rgba(0,194,165,1)");

    // Draw x-axis
    svg.append("g")
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .attr("color", "gray")
        .call(d3.axisBottom(x).ticks(Math.ceil(d3.max(data, d => d.count) / 20)));

    // Draw y-axis
    svg.append("g")
        .attr("transform", `translate(${margin.left},0)`)
        .attr("color", "gray")
        .attr("font-size", "10px")
        .call(d3.axisLeft(y));

    // Draw the bar
    const bar = svg.selectAll(".bar")
        .data(data)
        .enter().append("path")
        .attr("class", "bar")
        .attr("fill", "url(#gradient)")
        .attr("d", d => {
            const heightScaleFactor = 0.8;
            const newHeight = y.bandwidth() * heightScaleFactor;
            const newY = y(d.display_name) + (y.bandwidth() - newHeight) / 2;
            return rightRoundedRect(
                x(0),
                newY,
                0, // Initial width set to 0
                newHeight,
                10
            );
        })
        .transition() // Apply transition for bar width
        .duration(1000) // Animation duration in milliseconds
        .attr("d", d => {
            const heightScaleFactor = 0.8;
            const newHeight = y.bandwidth() * heightScaleFactor;
            const newY = y(d.display_name) + (y.bandwidth() - newHeight) / 2;
            return rightRoundedRect(
                x(0),
                newY,
                x(d.count) - x(0), // Transition to the actual width based on data
                newHeight,
                10
            );
        });

    return svg.node();
}

/**
 * Generates a vertical bar chart using D3.js.
 *
 * @param {Array} data - The data array where each element is an object containing 'count' and 'display_name' properties.
 * @param {string} id - The ID of the HTML element where the chart will be appended.
 * @param {string} text_id - The ID of the HTML element where the selected bar's information will be displayed.
 * @returns {Object} - The generated SVG node.
 */
export function vertical_bar_chart(data, id, text_id) {       
    const margin = { top: 50, right: 70, bottom: 25, left: 50 }; // Adjusted right margin for the legend

    let container = document.getElementById(id);
    let width = 420 //container.clientWidth; // Adjusted to fit container
    let height = 300 ; // Adjusted height

    // SVG
    const svg = d3.select(`#${id}`)
        .append("svg")
        .attr("width", "100%")  
        .attr("height", height) 
        // .attr("viewBox", "0 0 desiredWidth desiredHeight")
        .attr("preserveAspectRatio", "xMidYMid meet");

    // Axis
    const x = d3.scaleBand()
        .domain(data.map((d, i) => i)) // Use index as domain
        .range([margin.left, width - margin.right])
        .padding(0.1);

    const y = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.count)])
        .nice()
        .range([height - margin.bottom, margin.top]);

    const tooltip = d3.select(`#${id}`)
        .append('div')
        .style('position', 'absolute')
        .style('font-size', '12px')
        .style('opacity', 0) // Initially hidden
        .style('background', 'rgba(0, 0, 0, 0.6)')
        .style('color', '#fff')
        .style('border-radius', '5px')
        .style('padding', '5px');

    const bottom_name = d3.select(text_id);

    let selected_bar = null;

    // Draw x-axis
    svg.append("g")
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .attr("color", "gray")
        .call(d3.axisBottom(x).tickSizeOuter(0).tickFormat((d, i) => ""))
        .call(g => g.append("text")
            .attr("fill", "currentColor")
            .attr('x', width - margin.right - 30)
            .attr('y', 20)
            .attr("class", "card-sm-num text-center area_chart_x_title")
            .attr("text-anchor", "end")
            .style("font-size", "12px")
            .text("")
        );

    // Draw y-axis
    svg.append("g")
        .attr("transform", `translate(${margin.left},0)`)
        .attr("color", "gray")
        .attr("font-size", "10px")
        .call(d3.axisLeft(y).ticks(6))
        .call(g => g.append("text")
            .attr("x", -margin.left)
            .attr("y", 32)
            .attr("fill", "currentColor")
            .attr("text-anchor", "start")
            .attr("class", "card-sm-num text-center")
            .attr("data-i18n", "label-dataset-num")
            .style("font-size", "12px")
            .text(i18next.t('label-dataset-num'))
        );

    // Draw the bars
    svg.selectAll(".bar")
        .data(data)
        .enter().append("rect")
        .attr("class", "bar")
        .attr("fill", "url(#gradient)")
        .attr("x", (d, i) => x(i))
        .attr("y", d => y(0)) // Initial y position set to 0
        .attr("width", x.bandwidth())
        .attr("height", d => height - margin.bottom - y(0)) // Initial height set to 0
        
        // Events
        .on("mouseover", function(event, d) {
            d3.select(this)
                .attr('fill', 'url(#gradient2)');

            tooltip.style("opacity", 1)
        })
        .on("click", function (event, d) {
            if (!selected_bar) {
                selected_bar = this; // first select
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('fill', 'url(#gradient2)');
            } else {
                // If a different arc is clicked, update the selection
                if (selected_bar !== this) {
                    d3.select(selected_bar)
                        .transition()
                        .duration(200)
                        .attr('fill', 'url(#gradient)');

                    d3.select(this)
                        .transition()
                        .duration(200)
                        .attr('fill', 'url(#gradient2)');

                    selected_bar = this;
                } else {
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .attr('fill', 'url(#gradient2)');
                }
            }

            bottom_name.html(`
                <p class="card-big-num text-center fs-3" style="font-weight: 900">${d.count}</p>
                <p class="card-sm-num text-center">${d.display_name}</p>
            `);

            if (d.zh_tw_label && d.zh_tw_label.value !== undefined) {
                bottom_name.append('p')
                    .attr('class', 'card-sm-num text-center')
                    .text(d.zh_tw_label.value);
            }
        })
        .on("mousemove", function(event, d) {
            const rect = d3.select(this);
            const x = +rect.attr('x');
            const y = +rect.attr('y');
            const width = +rect.attr('width');

            tooltip.style("opacity", 1)
                .text(d.display_name)
                .style("left", `${(x + width)/2}px`)
                .style("bottom", `${(height + y)/2}px`);
        })
        .on("mouseout", function(event, d) {
            if(selected_bar !== this) {
                d3.select(this)
                .attr('fill', 'url(#gradient)'); 
            }

            tooltip.style("opacity", 0);
        })
        
        // init animation
        .transition() // Apply transition for bar height
        .duration(1000) // Animation duration in milliseconds
        .attr("y", d => y(d.count)) // Transition to the actual y position based on data
        .attr("height", d => height - margin.bottom - y(d.count)) // Transition to the actual height based on data;

    return svg.node();
}

/**
 * Updates a vertical bar chart with new data.
 * 
 * @param {Array} data - The new data to update the chart with.
 * @param {string} container_id - The ID of the container element for the chart.
 * @param {string} text_id - The ID of the element to display text in the chart.
 * @returns {void}
 */
export function update_vertical_bar_chart(data, container_id, text_id) {
    const svg = d3.select(`#${container_id} svg`);
    // Create
    if (svg.empty()) {
        return vertical_bar_chart(data, container_id, text_id);
    } 
    // Update
    else {
        svg.remove();
        return vertical_bar_chart(data, container_id, text_id);
    }
}


/**
 * Renders a vertical bar chart with labels.
 * 
 * @param {string} container_id - The ID of the container element where the chart will be rendered.
 * @param {Array} data - The data used to generate the chart. Each element in the array should have a `display_name` and `value` property.
 * @returns {Element} - The SVG element containing the rendered chart.
 */
export function v_bar_chart_with_label(container_id, data) {
    const margin = { top: 40, right: 70, bottom: 100, left: 40 };
    let width = 1200;
    let height = 400;

    // SVG
    const svg = d3.select(`#${container_id}`)
        .append("svg")
        .attr("width", "100%")
        .attr("height", height)
        .attr("preserveAspectRatio", "xMidYMid meet")
        .style("-webkit-tap-highlight-color", "transparent")
        .style("overflow", "visible");

    // Axis
    const x = d3.scaleBand()
        .domain(data.map(d => d.display_name))
        .range([margin.left, width - margin.right])
        .padding(0.05);

    const y = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.value)])
        .nice()
        .range([height - margin.bottom, margin.top]);

    // Draw y-axis
    svg.append("g")
        .attr("transform", `translate(${margin.left},0)`)
        .attr("color", "gray")
        .attr("font-size", "10px")
        .call(d3.axisLeft(y).ticks(6))
        .call(g => g.select(".domain").remove())
        .call(g => g.selectAll(".tick line").clone()
            .attr("x2", width - margin.left - margin.right)
            .attr("stroke-opacity", 0.1))
        .call(g => g.append("text")
            .attr("x", -margin.left)
            .attr("y", 10)
            .attr("fill", "currentColor")
            .attr("text-anchor", "start"));

    // Draw the bar
    let bars = svg.selectAll(".bar")
        .data(data)
        .enter().append("path")
        .attr("class", "bar")
        .attr("fill", "url(#gradient)")
        .attr("d", d => {
            const xPosition = x(d.display_name) + x.bandwidth() * 0.3;
            const yPosition = y(0);
            const barWidth = x.bandwidth() * 0.7;
            const barHeight = height - margin.bottom - y(0);
            const radius = 12;
            return topRoundedRect(xPosition, yPosition, barWidth, barHeight, radius);
        });

        // Init animation
        bars
        .transition()
        .duration(1000)
        .attr("d", d => {
            const xPosition = x(d.display_name) + x.bandwidth() * 0.1;
            const yPosition = y(d.value);
            const barWidth = x.bandwidth() * 0.7;
            const barHeight = height - margin.bottom - y(d.value);
            const radius = 12;
            return topRoundedRect(xPosition, yPosition, barWidth, barHeight, radius);
        });

        // Add mouse events
        bars
        .on("mouseover", function(event, d) {
            const [xPos, yPos] = [x(d.display_name) + x.bandwidth() / 2, y(d.value) - 5];

            const top_value = svg.select(`.clicked[data-display-name="${d.display_name}"]`);
            if (top_value.empty()) {
                svg.append("text")
                    .attr("class", "value-label")
                    .attr("x", xPos)
                    .attr("y", yPos)
                    .attr("text-anchor", "middle")
                    .attr("fill", "gray")
                    .style("font-weight", "bold")
                    .style("font-size", "14px")
                    .text(`${(d.value * 100).toFixed(0)}%`);
            }
            
            d3.select(this)
                .attr('fill', 'url(#gradient2)');
        })
        .on("click", function(event, d) {
            const [xPos, yPos] = [x(d.display_name) + x.bandwidth() / 2, y(d.value) - 5];
            const top_value = svg.select(`.clicked[data-display-name="${d.display_name}"]`);

            if (top_value.empty()) {
                svg.append("text")
                    .attr("class", "clicked")
                    .attr("data-display-name", d.display_name)
                    .attr("x", xPos)
                    .attr("y", yPos)
                    .attr("text-anchor", "middle")
                    .attr("fill", "rgba(0,194,165,1)")
                    .style("font-weight", "bold")
                    .style("font-size", "14px")
                    .text(`${(d.value * 100).toFixed(0)}%`);
            } else {
                top_value.remove();
            }
        })
        .on("mouseout", function(event, d) {
            svg.select(".value-label").remove();

            d3.select(this)
                .attr('fill', 'url(#gradient)'); 
        });

    // Draw x-axis
    svg.append("g")
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .attr("color", "gray")
        .call(d3.axisBottom(x).tickSizeOuter(0))
        .selectAll("text")
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .attr("transform", "rotate(-45)")
            .style("font-weight", "bold")
            .style("font-size", "12px")
            .each(function(_, i) {
                const label = d3.select(this).text();
                const matched = data.find(d => d.display_name === label);
                if (matched) {
                    d3.select(this)
                        .attr("data-i18n", `md_${matched.key}`)
                        .text('');
                }
            });
    
    setTimeout(() => {
        $('[data-i18n]').localize();
    }, 0);

    return svg.node();
}

/**
 * Updates or creates a vertical bar chart with labels using D3.js.
 *
 * @param {Array} data - The data array where each element is an object containing 'count' and 'display_name' properties.
 * @param {string} container_id - The ID of the HTML element where the chart will be appended.
 * @param {string} text_id - The ID of the HTML element where the selected bar's information will be displayed.
 * @returns {SVGElement}} - The generated or updated SVG element containing the bar chart.
 */
export function update_v_bar_chart_with_label(data, container_id, text_id) {
    const svg = d3.select(`#${container_id} svg`);
    // Create
    if (svg.empty()) {
        return v_bar_chart_with_label(data, container_id, text_id);
    } 
    // Update
    else {
        svg.remove();
        return v_bar_chart_with_label(data, container_id, text_id);
    }
}